import React, { useEffect, useRef, useState } from 'react';

import config from 'config';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import FilterIcon from '~/icons/components/FilterIcon';
import Link from '~/shared/components/Link';
import { useStickyHeader } from '~/shared/utils/StickyHeaderContext';
import {
  FableticsMarkLogo,
  SavageMarkLogo,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { useFiltersPanel } from '../context/FiltersPanelContext';
import useIsHiddenBehind from '../hooks/useIsHiddenBehind';
import useTabsAndBreadcrumbs from '../hooks/useTabsAndBreadcrumbs';

import Breadcrumbs from './Breadcrumbs';
import CategoryTabs from './CategoryTabs';
import SortDropdown from './SortDropdown';

const NavContent = styled.div<{
  $visible: boolean;
  $variant: 'desktop' | 'mobile';
}>`
  display: flex;
  flex-direction: ${({ $variant }) =>
    $variant === 'mobile' ? 'column' : 'row'};
  align-items: ${({ $variant }) =>
    $variant === 'mobile' ? 'stretch' : 'center'};
  padding: 0 ${({ $variant }) => ($variant === 'mobile' ? '16px' : '32px')};
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.gridV2NavHeaderBorder};
  max-height: ${({ $visible }) => ($visible ? '200px' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  overflow: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition:
    max-height 0.5s ease,
    opacity 0.5s ease;
`;

const InnerContent = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 0;
  width: 100%;
`;

const LogoLink = styled(Link)<{ $logoVisible: boolean }>`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  flex-shrink: 0;
  width: ${({ $logoVisible }) => ($logoVisible ? '28px' : '0')};
  transform: scale(${({ $logoVisible }) => ($logoVisible ? 1 : 0)});
  transform-origin: center;
  overflow: hidden;
  transition:
    width 0.3s ease,
    transform 0.3s ease;

  svg {
    width: 28px;
  }
`;

const BreadcrumbWrapper = styled.div<{ $logoVisible: boolean }>`
  display: flex;
  align-items: center;
  margin-left: ${({ $logoVisible }) => ($logoVisible ? '16px' : '0')};
  transition: margin-left 0.3s ease;

  /* Breadcrumbs' inner container hardcodes font-weight: 350, so inheriting
     from this wrapper doesn't override it. Target the child directly to bold
     the breadcrumbs only when rendered inside the sticky scroll header. */
  > div {
    font-weight: 700;
  }
`;

const NumResultsText = styled.span<{ $visible: boolean }>`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gridV2NumResultsText};
  white-space: nowrap;
  padding-left: 8px;
  max-width: ${({ $visible }) => ($visible ? '200px' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  overflow: hidden;
  transition:
    max-width 0.3s ease,
    opacity 0.3s ease;
`;

const RightControls = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '-4px')});
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease,
    visibility 0s linear ${({ $visible }) => ($visible ? '0s' : '0.3s')};

  /* Override SortDropdown's default 48px height and always-visible border to
     match the scroll-header variant: 40px with outline only on hover. */
  button {
    height: 40px;
    border-color: transparent;
    transition:
      background-color 0.2s,
      border-color 0.2s;
  }

  button:hover {
    border-color: ${({ theme }) => theme.colors.gridV2FilterBtnBorder};
  }
`;

const AllFiltersButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 16px;
  height: 40px;
  border: 0.5px solid transparent;
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  white-space: nowrap;
  transition:
    background-color 0.2s,
    border-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gridV2FilterBtnBgHover};
    border-color: ${({ theme }) => theme.colors.gridV2FilterBtnBorder};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.gridV2FilterBtnBgActive};
  }
`;

const MobileRow = styled.div<{ $hasTabs: boolean }>`
  display: flex;
  align-items: center;
  /* Asymmetric padding (top > bottom) only when tabs follow, so the tabs'
     own top padding doesn't double up. Without tabs, keep it symmetric. */
  padding: ${({ $hasTabs }) => ($hasTabs ? '12px 0 8px' : '12px 0')};
  width: 100%;
`;

const MobileNumResults = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gridV2NumResultsText};
  white-space: nowrap;
  margin-left: auto;
`;

const CategoryTabsWrapper = styled.div<{ $visible: boolean }>`
  margin: 0 -16px;
  max-height: ${({ $visible }) => ($visible ? '60px' : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  overflow: hidden;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
`;

interface GridScrollHeaderProps {
  variant?: 'desktop' | 'mobile';
  breadcrumbsRef: React.RefObject<HTMLDivElement>;
  filterBarRef?: React.RefObject<HTMLDivElement>;
  numResultsRef: React.RefObject<HTMLDivElement>;
  totalNumResults?: number;
}

const GridScrollHeader = ({
  variant = 'desktop',
  breadcrumbsRef,
  filterBarRef,
  numResultsRef,
  totalNumResults,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
GridScrollHeaderProps): any => {
  const { stickyPortalRef } = useStickyHeader();
  const { openFiltersPanel, isFiltersPanelOpen } = useFiltersPanel();
  const { tabs } = useTabsAndBreadcrumbs();
  const hasTabs = Boolean(tabs?.length);
  const navContentRef = useRef<HTMLDivElement>(null);
  const isBreadcrumbsHidden = useIsHiddenBehind(breadcrumbsRef, navContentRef);
  const isFilterBarHidden = useIsHiddenBehind(filterBarRef, navContentRef);
  const isNumResultsHidden = useIsHiddenBehind(numResultsRef, navContentRef);
  const [isMainHeaderVisible, setIsMainHeaderVisible] = useState(false);
  const lastScrollYRef = useRef(
    typeof window !== 'undefined' ? window.scrollY : 0
  );
  const showSavageMarkLogo = config.get(
    'public.brand.features.showSavageMarkLogo'
  );

  // Track scroll direction to know if main header is visible
  // Skip updates when filters panel is open to freeze the logo state
  useEffect(() => {
    if (isFiltersPanelOpen) {
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingUp = currentScrollY < lastScrollYRef.current;
      lastScrollYRef.current = currentScrollY;
      setIsMainHeaderVisible(scrollingUp);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isFiltersPanelOpen]);

  if (!stickyPortalRef.current) {
    return null;
  }

  const showLogo = !isMainHeaderVisible;

  // Prevent focus/blur events from bubbling to the Sticky wrapper's autoHide
  // handlers. Without this, clicking buttons in the GridScrollHeader would
  // momentarily set isFocused=true on the Sticky, toggling the main header.
  const stopFocusPropagation = (e: React.FocusEvent) => e.stopPropagation();

  return createPortal(
    <NavContent
      ref={navContentRef}
      $visible={isBreadcrumbsHidden}
      $variant={variant}
      onFocus={stopFocusPropagation}
      onBlur={stopFocusPropagation}
    >
      {variant === 'mobile' ? (
        <>
          <MobileRow $hasTabs={hasTabs}>
            <Breadcrumbs />
            {totalNumResults ? (
              <MobileNumResults>[{totalNumResults}]</MobileNumResults>
            ) : null}
          </MobileRow>
          <CategoryTabsWrapper $visible>
            <CategoryTabs hideEdgeFade />
          </CategoryTabsWrapper>
        </>
      ) : (
        <InnerContent>
          <LogoLink href="/" passHref $logoVisible={showLogo}>
            {showSavageMarkLogo ? <SavageMarkLogo /> : <FableticsMarkLogo />}
          </LogoLink>
          <BreadcrumbWrapper $logoVisible={showLogo}>
            <Breadcrumbs compact />
            {totalNumResults && (
              <NumResultsText $visible={isNumResultsHidden}>
                [{totalNumResults}]
              </NumResultsText>
            )}
          </BreadcrumbWrapper>
          <RightControls $visible={isFilterBarHidden}>
            <AllFiltersButton type="button" onClick={openFiltersPanel}>
              <FilterIcon />
              <FormattedMessage
                id="grid_static.all_filters"
                defaultMessage="All Filters"
              />
            </AllFiltersButton>
            <SortDropdown />
          </RightControls>
        </InnerContent>
      )}
    </NavContent>,
    stickyPortalRef.current
  );
};

export default GridScrollHeader;
